import { ReactElement } from "react";
import { useSelector } from "react-redux";
import { useI18n } from "i18n";
import { Tooltip } from "@remo-co/ui-core/src/components/Tooltip";
import { selectIsMobile } from "modules/deviceInfo";
import { useLockTable } from "modules/lockTables/hooks";
import { SVGRoomNames } from "modules/customFloorPlan/constants";
import { selectCurrentRoomId } from "store/rooms";
import { useStyles } from "./styles";

interface Props {
  tableId: string;
  code: string;
  isEventManager: boolean;
  children: ReactElement;
  isLockTablesEnabled: boolean;
}

const RoomComponentWrapper = ({
  tableId,
  children,
  code,
  isEventManager,
  isLockTablesEnabled,
}: Props): JSX.Element => {
  const isMobileView = useSelector(selectIsMobile);
  const currentTable = useSelector(selectCurrentRoomId);
  const styles = useStyles();
  const isCurrentTable = currentTable === tableId;
  const { t } = useI18n(["template", "userLocator"]);

  const { lockedTables } = useLockTable();
  const isStage = code === SVGRoomNames.STAGE;
  const getGuestHoverText = (): string => {
    if (isMobileView) {
      return "";
    }
    if (isCurrentTable) {
      return t("userLocator:you.are.here");
    }
    if (!isStage) {
      const isTableEntryLocked = lockedTables.some(
        (table) => table.table === tableId && table.denyEntry,
      );
      const isTableExitLocked = lockedTables.some(
        (table) => table.table === currentTable && table.denyExit,
      );

      if (isTableExitLocked && isLockTablesEnabled) {
        return t("template:cannot.leave.current.table");
      }
      if (isTableEntryLocked && isLockTablesEnabled) {
        return t("template:table.is.locked");
      }

      return t("template:double.click.join");
    }

    return t("template:host.invite.required");
  };

  const getEventManagerHoverText = () => {
    if (isMobileView) {
      return "";
    }
    if (isCurrentTable) {
      return t("userLocator:you.are.here");
    }
    return t("template:double.click.join");
  };

  const tooltip = isEventManager
    ? getEventManagerHoverText()
    : getGuestHoverText();

  return (
    <Tooltip
      data-testid="room-tooltip"
      title={tooltip}
      placement="top"
      disableTouchListener
      disableInteractive
      aria-hidden
      PopperProps={{
        sx: {
          zIndex: 1,
        },
      }}
      classes={{
        tooltip: styles.tooltipPopper,
      }}
    >
      {children}
    </Tooltip>
  );
};

export default RoomComponentWrapper;
